/* eslint-disable eqeqeq */
/* eslint-disable no-eq-null */
/* eslint-disable no-param-reassign */
import Apis from '@forchange/apis';
import { isBrowser } from './isBrowser';
import { getTokenKey, getToken, parseQuery, setCookie, removeCookie } from '@forchange/utils';

import env from '../config/env';
import {
  serverMap,
  serverSuffix,
  combineMode,
  IServerSuffix,
  IServerMap,
  IBaseConfig
} from '../config/apis';

const apiMap = {};

interface IApiMap {
  [key: string]: {
    server?: string;
    url: string;
    method: 'get' | 'post' | 'put' | 'delete';
  };
}

// 本地local配置
mergeLocalConfig();

// 整理 apiMap
setApiMap(apiMap);
// 整理 serverMap
setServerMap(serverMap);

function mergeLocalConfig() {
  if (env.ENV !== 'local') return;

  try {
    const localConfigModule = require.context('../config/apis/.local', false, /local\.config\.js$/);
    let localConfig: any = '';

    localConfigModule.keys().forEach((key) => {
      localConfig = localConfigModule(key);
    });

    if (localConfig) {
      const localServerMap: Record<string, IBaseConfig> = localConfig.localServerMap;
      const localServerSuffix: IServerSuffix = localConfig.localServerSuffix;

      localServerMap &&
        Object.keys(localServerMap).forEach((server) => {
          serverMap[server].baseMap = {
            ...serverMap[server].baseMap,
            local: localServerMap[server]
          };
        });

      localServerSuffix && Object.assign(serverSuffix, { local: localServerSuffix });
    }
  } catch (e) {
    console.error(e);
  }
}

function setApiMap(apiMap: IApiMap) {
  try {
    const model = require.context(`../server`, true, /.*\/apis\.ts?$/);
    const pages = require.context(`../pages`, true, /.*\/apis\.tsx?$/);
    const components = require.context(`../components`, true, /.*\/apis\.tsx?$/);

    model.keys().forEach((key: string) => {
      const config = model(key).default;
      Object.assign(apiMap, config);
    });
    pages.keys().forEach((key: string) => {
      const config = pages(key).default;
      Object.assign(apiMap, config);
    });

    components.keys().forEach((key: string) => {
      const config = components(key).default;
      Object.assign(apiMap, config);
    });
  } catch (err) {
    console.error(err.message);
  }
}

function setServerMap(serverMap: IServerMap) {
  const suffix = getServerSuffix();

  for (const key of Object.keys(serverMap)) {
    const serverConfig = serverMap[key].baseMap[env.ENV];
    let baseURL = combineURL(serverConfig);

    // 兼容处理，CI/CD CI_DEV_BRANCH 返回的名称不区分大小写
    const suffixContent = suffix?.[env.ENV]?.[key] ?? suffix?.[env.ENV]?.[key.toLocaleLowerCase()];

    if (suffixContent) {
      if (combineMode === 'path') {
        // 按照URL branch 规则，使用 - 拼接
        baseURL = `${baseURL}-${suffixContent}`;
      } else {
        // combineMode !== path 忽略suffix，原因是域名将被统一，不再区分feature/fix
        console.info('warning: combineMode: domain模式下 忽略suffix/CI_DEV_BRANCH');
      }
    }

    if (isBrowser()) {
      // host
      const dHost = parseQuery<string>('host');
      if (dHost) {
        baseURL = dHost;
      }
    }

    serverMap[key].baseURL = baseURL;
  }

  function getServerSuffix(): IServerSuffix {
    // eg: -feat-baseServer-refactor-mp-info
    const branchNameRegExp = /^-?([^-]+)-([^-]+)(-[\w\d-]+)$/;

    if (Object.keys(serverSuffix).length) {
      return serverSuffix;
    }

    if (env.ENV !== 'dev') return {};

    if (!branchNameRegExp.test(process.env.CI_DEV_BRANCH!)) {
      console.warn('serverMap: 分支名不合法，请按 (feat|fix)/$module_$feature 方式命名');

      const key = Object.keys(serverMap).find((serverKey) => serverMap[serverKey].default);

      if (!key) return {};

      return {
        dev: {
          [key]: process.env.CI_DEV_BRANCH!
        }
      };
    }

    const config = process.env.CI_DEV_BRANCH!.match(branchNameRegExp);

    const [_, suffix, serverName, moduleName] = config!;
    return {
      dev: {
        [serverName]: suffix + moduleName
      }
    };
  }

  function combineURL(serverConfig?: IBaseConfig): string {
    // host 层级
    const level = 3;

    const serverConditions = [
      function protocol(url: string) {
        if (serverConfig?.protocol) {
          url += serverConfig.protocol + '//';
        } else if (isBrowser()) {
          url += location.protocol + '//';
        } else {
          if (process.env.ENV !== 'PROD') {
            console.log('SSR: 请完善serverMap 配置， protocol为必填项');
          }
        }
        return url;
      },
      function subDomain(url: string) {
        if (serverConfig?.subDomain) {
          url += serverConfig.subDomain + '.';
        } else if (serverConfig?.subDomain == null) {
          if (isBrowser()) {
            const hostLevel = location.hostname.split('.');
            // host 层级大于等于 3 将最左边的设为subDomain
            if (hostLevel.length >= level) {
              const [subDomain] = hostLevel;
              url += subDomain + '.';
            }
          } else {
            if (process.env.ENV !== 'PROD') {
              console.log('SSR: 请完善serverMap 配置， serverMap subDomain 不能为null');
            }
          }
        }
        return url;
      },
      function domain(url: string) {
        if (serverConfig?.domain) {
          url += serverConfig.domain;
        } else if (serverConfig?.domain == null) {
          if (isBrowser()) {
            const hostLevel = location.hostname.split('.');
            if (hostLevel.length >= level) {
              const [_, ...domain] = hostLevel;
              url += domain.join('.');
            } else {
              url += location.hostname;
            }
          } else {
            if (process.env.ENV !== 'PROD') {
              console.log('SSR: 请完善serverMap 配置， serverMap domain 不能为null');
            }
          }
        }

        return url;
      },
      function port(url: string) {
        if (serverConfig?.port) {
          url += ':' + serverConfig.port;
        } else if (serverConfig?.port == null) {
          if (isBrowser()) {
            url += location.port ? ':' + location.port : '';
          } else {
            if (process.env.ENV !== 'PROD') {
              console.log('SSR: 请完善serverMap 配置， serverMap port 不能为null');
            }
          }
        }

        return url;
      },
      function path(url: string) {
        if (serverConfig?.path) {
          url += serverConfig.path.replace(/\/$/, '');
        }
        return url;
      }
    ];

    return serverConditions.reduce((url, condition) => {
      return condition(url);
    }, '');
  }
}

const apis = Apis.create<ApisKeys>(serverMap, apiMap);

if (isBrowser()) {
  apis.useReq((config) => {
    config.withCredentials = true;
    let token = getToken();

    // token
    const dToken = parseQuery<string>('token');
    if (dToken) {
      token = dToken;
      setCookie(getTokenKey(), token);
    }

    // clear
    const dClear = parseQuery('clear');
    if (dClear) {
      token = '';
      removeCookie(getTokenKey());
    }

    // if (token) {
    //   config.headers.Authorization = `Bearer ${token}`;
    // }

    return config;
  });

  apis.useRes(
    async (res) => res.data,
    async (err) => {
      console.log(err);

      return Promise.reject(err);
    }
  );
}

export default apis;
